import { useMemo, useState } from 'react';
import {
	Grid,
	GridItem,
	H3,
	Input,
	Panel,
	Text,
	Link,
	Box,
} from '@bigcommerce/big-design';
import { CloudUploadIcon, ReceiptIcon, AssignmentIcon, StoreIcon } from '@bigcommerce/big-design-icons';
import { AddressText } from './AddressText';
import './CustomerInfo.css';
import { CustomerInfoEditForm } from './CustomerInfoEditForm';
import CopyableLink from '../../../../components/CopyableLink';
import CopyableText from '../../../../components/CopyableText';
import CardIcon from '../../../../images/card.png';
import EmailIcon from '../../../../images/email.png';
import PhoneIcon from '../../../../images/phone.png';
import ShippingIcon from '../../../../images/shipping.png';
import { formatOrderDate } from '../../../../utils/formatOrderDate';
import { ORDER_ACTIONS } from '../hooks/useOrderEditState';

export default function CustomerInfo(props) {
	const { custom, order, orderDispatch, prevOrders} = props;
	const { billing_address, shippingAddresses, payment_method } = order;
	const shippingAddress = shippingAddresses[0];
	const hasAddons = useMemo(() => {
		// Prefer checking via custom.products to mirror Products table logic
		if (Array.isArray(custom?.products) && Array.isArray(order?.products)) {
			for (const product of custom.products) {
				const bcOrderProduct = order.products.find(
					({ id }) => id === product.bc_order_item_id,
				);
				const optionsEntry = bcOrderProduct?.product_options?.find(
					({ display_name }) =>
						display_name?.toLowerCase() === 'options',
				);
				if (!optionsEntry?.value) continue;
				try {
					const parsed = JSON.parse(optionsEntry.value);
					if (parsed?.parentId) return true; // a child addon exists
				} catch (e) {
					// ignore JSON parse errors
				}
			}
		}

		// Fallback: scan raw order products for child addons
		if (Array.isArray(order?.products)) {
			return order.products.some((product) => {
				const optionsEntry = product?.product_options?.find(
					({ display_name }) =>
						display_name?.toLowerCase() === 'options',
				);
				if (!optionsEntry?.value) return false;
				try {
					const parsed = JSON.parse(optionsEntry.value);
					return Boolean(parsed?.parentId);
				} catch (e) {
					return false;
				}
			});
		}

		return false;
	}, [custom, order]);
	// TODO: Figure out why this isn't used
	const [internalTrackingNumber, setInternalTrackingNumber] = useState(
		custom.internal_tracking_number || '',
	);
	// TODO: Figure out why this isn't used
	const [shipWithOrder, setShipWithOrder] = useState(
		custom.ship_with || '',
	);
	const [editCustomerAddress, setEditCustomerAddress] = useState(false);
	const [addressObj, setAddress] = useState({});
	const [title, setTitle] = useState('');

	function updateCustomerInfo(address, title) {
		if (title === 'Billing') {
			orderDispatch({
				type: ORDER_ACTIONS.UPDATE_CUSTOMER_BILLING,
				data: address,
			});
		}
		if (title === 'Shipping') {
			orderDispatch({
				type: ORDER_ACTIONS.UPDATE_CUSTOMER_SHIPPING,
				data: address,
			});
		}

		setEditCustomerAddress(null);
		setAddress({});
		setTitle('');
	}

	function updateShipWithOrder(value) {
		orderDispatch({
			type: ORDER_ACTIONS.UPDATE_SHIP_WITH,
			data: value,
		});

		setShipWithOrder(value);
	}

	function updateInternalTrackingNumber(value) {
		orderDispatch({
			type: ORDER_ACTIONS.UPDATE_INTERNAL_TRACKING_NUMBER,
			data: value,
		});

		setInternalTrackingNumber(value);
	}

	function scrollToPreviousOrders() {
		window.scrollTo({
			top: document.getElementById('previousOrders').offsetTop - 200,
			behavior: 'smooth',
		});
		const previousOrdersEl = document.getElementById('previousOrders');
		if (previousOrdersEl) {
			previousOrdersEl.animate(
				[
					{ boxShadow: '0 0 5px 5px rgba(0, 123, 255, 0.3)', background: 'rgba(0, 123, 255, 0.1)' },
					{ boxShadow: '', background: '' },
				],
				{ duration: 3000, fill: 'forwards' },
			);
		}
	}

	return (
		<>
			<Panel header="Customer Information">
				<Grid
					marginVertical="small"
					gridColumns="repeat(auto-fit, minmax(24rem, 1fr))"
					gridAutoFlow="dense"
				>
					<GridItem>
						<Link
							className="addressLink"
							onClick={() => {
								setEditCustomerAddress(true);
								setAddress(billing_address);
								setTitle('Billing');
							}}
						>
							<dl className="definitionList">
								<dt>
									<H3>Billing</H3>
								</dt>
								<AddressText address={billing_address} />

								<dt>
									<img
										className="icon-image"
										src={PhoneIcon}
										alt="Phone icon"
									/>
								</dt>
								<dd>
									<Text>
										{billing_address?.phone
											? billing_address.phone
											: 'N/A'}
									</Text>
								</dd>

								<dt>
									<img
										className="icon-image"
										src={EmailIcon}
										alt="Email icon"
									/>
								</dt>
								<dd>
									<Text as="span">
										{billing_address?.email}
									</Text>
								</dd>

								<dt>
									<img
										className="icon-image"
										src={CardIcon}
										alt="Payment icon"
									/>
								</dt>
								<dd>
									<Text>
										{payment_method
											? payment_method
											: 'Manual'}
										{payment_method === 'Amazon'
											? ` Order ID: ${order.external_id}`
											: ''}
										{payment_method === 'Amazon'
											? ` Ship by: ${custom.ship_by}`
											: ''}
									</Text>
								</dd>
							</dl>
						</Link>
					</GridItem>

					<GridItem>
						<Link
							className="addressLink"
							onClick={() => {
								setEditCustomerAddress(true);
								setAddress(shippingAddress);
								setTitle('Shipping');
							}}
						>
							<dl className="definitionList">
								<dt>
									<H3>Shipping</H3>
								</dt>
								<AddressText address={shippingAddress} />

								<dt>
									<img
										className="icon-image"
										src={ShippingIcon}
										alt="Shipping icon"
									/>
								</dt>
								<dd>
									<Text>
										{shippingAddress.shipping_method}
									</Text>
								</dd>

								<dt />
								<dd />

								<dt />
								<dd />
							</dl>
						</Link>
					</GridItem>

					<GridItem>
						<dl className="definitionList">
							<dt>
								<H3>Additional Information</H3>
							</dt>
							<dd>
								<Input
									label="Internal Tracking Number"
									value={internalTrackingNumber || ''}
									onChange={(e) =>
										updateInternalTrackingNumber(
											e.target.value,
										)
									}
								/>
								<Input
									label="Ship With Order"
									type="text"
									value={shipWithOrder || ''}
									onChange={(e) =>
										updateShipWithOrder(e.target.value)
									}
								/>
							</dd>

							<dt>
								<ReceiptIcon size="medium" />
							</dt>
							<dd>
								<Text>Order Date: {formatOrderDate(order.date_created)}</Text>
							</dd>
							<dt>
								<AssignmentIcon size="medium" />
							</dt>
							<dd>
								<CopyableText
									text={order.estimatedShippingDate}
									label="Est. Ship Date"
								/>
							</dd>
							<dt>
								<CloudUploadIcon size="medium" />
							</dt>
							<dd>
								<a
									href={`https://www.shearcomfort.com/order-images?orderId=${order.id}`}
									target={'_blank'}
									rel="noreferrer"
								>
									Photo Upload Link
								</a>
								&nbsp;&nbsp;|&nbsp;&nbsp;
								<CopyableLink
									link={`https://www.shearcomfort.com/order-images?orderId=${order.id}`}
									label="copy link"
								/>
							</dd>
							<dt>
								<StoreIcon size="medium" />
							</dt>
							<dd>
								<a 
									href={`https://www.shearcomfort.com/your-gift-card?orderid=${order.id}`}
									target={'_blank'}
									rel="noreferrer"
								>
									Gift Card Link
								</a>
								&nbsp;&nbsp;|&nbsp;&nbsp;
								<CopyableLink
									link={`https://www.shearcomfort.com/your-gift-card?orderid=${order.id}`}
									label="copy link"
								/>
							</dd>
						</dl>
					</GridItem>
					{editCustomerAddress && (
						<CustomerInfoEditForm
							address={addressObj}
							title={title}
							onCancel={() => setEditCustomerAddress(false)}
							onComplete={updateCustomerInfo}
						/>
					)}
				</Grid>
				<Grid marginTop="Large">
					<GridItem>
						{prevOrders?.length > 0 && (
						<Box marginTop="Large" style={{ textAlign: 'center' }}>
							<Link className="previousOrderOnFile" onClick={() => scrollToPreviousOrders()}>PREVIOUS ORDERS ON FILE</Link>
						</Box>
						)}

						{hasAddons && (
							<Box marginTop="Large" style={{ textAlign: 'center' }}>
								<div className="addonsDetected">Addons Detected</div>
							</Box>
						)}
					</GridItem>
				</Grid>
			</Panel>
		</>
	);
}
